import { Injectable } from '@angular/core';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import { AbstractDataProvider } from '@epicuro-next/data-providers/base-data-provider';
import { ensureArray } from '@epicuro-next/utilities';
import { combineLatest, isObservable, map, Observable, of } from 'rxjs';

/**
 * All possible values that could be obtained using the provider.
 * If a value is added here, make sure to also extend the switch in the 'getDataByKey' method of the service.
 */
export enum UtilityDataKeys {
  IsMobileView = 'IsMobileView',
}

/**
 * Services to obtain utility like data.
 * At the time of writing it only provides a boolean to indicate whether the browser is rendering in mobile view or not.
 * It is designed that adding more utility like data is easy (see note on the enum above).
 */
@Injectable()
export class UtilitiesProviderService implements AbstractDataProvider {
  private readonly allKeysLoweredCase = Object.values(UtilityDataKeys).reduce(
    (acc, cur) => {
      acc[cur.toLowerCase()] = cur;
      return acc;
    },
    {} as { [key: string]: UtilityDataKeys },
  );

  /**
   * Returns the data requested by a key or array of keys.
   * @param keyOrKeys A single key or array of keys
   * @returns If a single key was provided it returns a single value.
   * If multiple keys were provided it returns an object where its keys represent
   * the keys provided as arguments to this method.
   * (See the unit test for more details on the behavior.)
   */
  public read<T>(keyOrKeys: string | string[]): Observable<T> {
    const isArray = Array.isArray(keyOrKeys);

    const keys = ensureArray(keyOrKeys);
    const validKeys: [UtilityDataKeys, string][] = this.getValidKeys(keys);

    if (validKeys.length === 0) {
      return of(isArray ? [] : undefined) as Observable<T>;
    }

    const values$: Observable<[string, unknown]>[] = this.getValues$(validKeys);

    if (values$.length === 1 && !isArray) {
      return values$[0].pipe(
        map(([, value]) => {
          return value;
        }),
      ) as Observable<T>;
    }

    return this.combineIntoSingleObservable(values$) as Observable<T>;
  }

  public write() {
    // for linter
  }

  private getValidKeys(userPutInKeys: string[]): [UtilityDataKeys, string][] {
    const keysToUse: [UtilityDataKeys, string][] = [];
    const invalidKeys: string[] = [];

    for (const userPutInKey of userPutInKeys) {
      const lowerCasedKey = userPutInKey.toLowerCase();
      const existingKey = this.allKeysLoweredCase[lowerCasedKey];
      if (existingKey) {
        keysToUse.push([existingKey, userPutInKey]);
      } else {
        invalidKeys.push(userPutInKey);
      }
    }

    if (invalidKeys.length > 0) {
      const allValidKeysStr = Object.values(this.allKeysLoweredCase).join(', ');
      const invalidKeysStr = invalidKeys.join(', ');
      console.warn(
        `There is no utility data known by the key(s) '${invalidKeysStr}'. Valid keys are: '${allValidKeysStr}'.`,
      );
    }

    return keysToUse;
  }

  private getDataByKey(key: UtilityDataKeys) {
    // tslint:disable-next-line:no-small-switch
    switch (key) {
      case UtilityDataKeys.IsMobileView:
        return atxIsMobile$;
      default:
        throw new Error(
          `No case was implemented for key '${key}' in UtilitiesProviderService.`,
        );
    }
  }

  private getValues$(validKeys: [UtilityDataKeys, string][]) {
    const values$: Observable<[string, unknown]>[] = [];

    for (const [validKey, userPutInKey] of validKeys) {
      let dataByKey: Observable<unknown> = this.getDataByKey(validKey);

      if (!isObservable(dataByKey)) {
        dataByKey = of(dataByKey);
      }

      const valueToUse: Observable<[string, unknown]> = dataByKey.pipe(
        map((v) => {
          return [userPutInKey, v];
        }),
      );

      values$.push(valueToUse);
    }

    return values$;
  }

  private combineIntoSingleObservable(
    values$: Observable<[string, unknown]>[],
  ): Observable<{
    [key in UtilityDataKeys]?: unknown;
  }> {
    return combineLatest(values$).pipe(
      map((keyValuePairs) => {
        const obj: {
          [key in UtilityDataKeys]?: unknown;
        } = {};

        for (const [key, value] of keyValuePairs) {
          obj[key as UtilityDataKeys] = value;
        }

        return obj;
      }),
    );
  }
}
